import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/app/PageHeader";
import { ROLE_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LockKey, UserCircle, Buildings, UploadSimple, Warning, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import BackupSettings from "@/components/app/BackupSettings";

export default function Settings() {
  const { user, isAdmin } = useAuth();
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const [company, setCompany] = useState(null);
  const [savingCo, setSavingCo] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPwd, setResetPwd] = useState("");
  const [resetting, setResetting] = useState(false);

  const doReset = async () => {
    if (!resetPwd) { toast.error("Enter your admin password to confirm"); return; }
    setResetting(true);
    try {
      const r = await api.post("/admin/factory-reset", { password: resetPwd });
      toast.success(`Reset complete — ${r.data.total} test records removed`);
      setResetOpen(false); setResetPwd("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setResetting(false); }
  };

  const loadCompany = useCallback(async () => {
    if (!isAdmin()) return;
    try { setCompany((await api.get("/settings/company")).data || {}); } catch { setCompany({}); }
  }, [isAdmin]);
  useEffect(() => { loadCompany(); }, [loadCompany]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("drive");
    if (!p) return;
    if (p === "connected") toast.success("Google Drive connected successfully");
    else if (p === "scope") toast.error("Google Drive permission was not granted");
    else toast.error("Google Drive connection failed");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const changePw = async () => {
    if (!cur || !nw) { toast.error("Fill all fields"); return; }
    if (nw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (nw !== cf) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password updated"); setCur(""); setNw(""); setCf(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const setCo = (k, v) => setCompany((p) => ({ ...p, [k]: v }));
  const saveCompany = async () => {
    setSavingCo(true);
    try { const r = await api.put("/settings/company", company); setCompany(r.data); toast.success("Company profile saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSavingCo(false); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/settings/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setCompany((p) => ({ ...p, logo: r.data.logo })); toast.success("Logo updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader overline="Settings" title="Settings" subtitle="Company profile, your account and security." />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        {isAdmin() && company && (
          <div className="border border-zinc-200 bg-white rounded-sm p-6 lg:col-span-2" data-testid="company-profile-card">
            <div className="flex items-center gap-2 mb-5 text-zinc-900"><Buildings size={20} weight="duotone" /><h3 className="font-heading text-base">Paisapaid Company Profile</h3></div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="h-24 w-24 rounded-sm border border-zinc-200 bg-zinc-50 grid place-items-center overflow-hidden">
                  {company.logo ? <img src={company.logo} alt="logo" className="h-full w-full object-contain" data-testid="company-logo-img" /> : <Buildings size={30} className="text-zinc-300" />}
                </div>
                <label className="cursor-pointer text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1.5" data-testid="company-logo-upload">
                  <UploadSimple size={15} /> Upload Logo
                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </label>
              </div>
              <div className="md:col-span-2 grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-zinc-500 mb-1.5 block">Company Name</Label><Input data-testid="co-name" value={company.company_name || ""} onChange={(e) => setCo("company_name", e.target.value)} className="rounded-sm h-10" /></div>
                <div><Label className="text-xs text-zinc-500 mb-1.5 block">Brand Name</Label><Input data-testid="co-brand" value={company.brand_name || ""} onChange={(e) => setCo("brand_name", e.target.value)} className="rounded-sm h-10" /></div>
                <div><Label className="text-xs text-zinc-500 mb-1.5 block">GSTIN</Label><Input data-testid="co-gstin" value={company.gstin || ""} onChange={(e) => setCo("gstin", e.target.value)} className="rounded-sm h-10" /></div>
                <div className="col-span-2"><Label className="text-xs text-zinc-500 mb-1.5 block">Address</Label><Textarea data-testid="co-address" value={company.address || ""} onChange={(e) => setCo("address", e.target.value)} className="rounded-sm" /></div>
                <div><Button data-testid="company-save" onClick={saveCompany} disabled={savingCo} className="rounded-sm bg-brand hover:bg-brand-dark">{savingCo ? "Saving…" : "Save Company Profile"}</Button></div>
              </div>
            </div>
          </div>
        )}

        <div className="border border-zinc-200 bg-white rounded-sm p-6">
          <div className="flex items-center gap-2 mb-5 text-zinc-900"><UserCircle size={20} weight="duotone" /><h3 className="font-heading text-base">Account</h3></div>
          <Row label="Name" value={user?.name} />
          <Row label="User ID" value={user?.login_id || user?.email} />
          <Row label="Role" value={ROLE_LABEL(user?.role)} />
          <Row label="Department" value={user?.department || "—"} />
          <Row label="Designation" value={user?.designation || "—"} />
        </div>

        <div className="border border-zinc-200 bg-white rounded-sm p-6">
          <div className="flex items-center gap-2 mb-5 text-zinc-900"><LockKey size={20} weight="duotone" /><h3 className="font-heading text-base">Change Password</h3></div>
          <div className="space-y-4">
            <div><Label className="text-xs text-zinc-500 mb-1.5 block">Current Password</Label><Input data-testid="cur-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-sm h-10" /></div>
            <div><Label className="text-xs text-zinc-500 mb-1.5 block">New Password</Label><Input data-testid="new-password" type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="rounded-sm h-10" /></div>
            <div><Label className="text-xs text-zinc-500 mb-1.5 block">Confirm New Password</Label><Input data-testid="confirm-password" type="password" value={cf} onChange={(e) => setCf(e.target.value)} className="rounded-sm h-10" /></div>
            <Button data-testid="change-password-button" onClick={changePw} disabled={busy} className="rounded-sm bg-brand hover:bg-brand-dark">{busy ? "Updating…" : "Update Password"}</Button>
          </div>
        </div>

        {isAdmin() && <BackupSettings />}

        {isAdmin() && (
          <div className="border-2 border-red-200 bg-red-50/40 rounded-sm p-6 lg:col-span-2" data-testid="danger-zone-card">
            <div className="flex items-center gap-2 mb-2 text-red-700"><Warning size={20} weight="duotone" /><h3 className="font-heading text-base">Danger Zone — Reset All Data</h3></div>
            <p className="text-sm text-zinc-600 max-w-2xl mb-4">Permanently deletes ALL business data on this environment — disbursements, Own Brand sales, expenses, employees, agents, partners, lenders, products, agreements, development forms, invoices, payouts, documents and logs. Your admin account, company settings and default agreement templates are kept. This cannot be undone.</p>
            <Button data-testid="factory-reset-open" variant="outline" onClick={() => setResetOpen(true)} className="rounded-sm border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"><Trash size={16} className="mr-1.5" /> Reset All Data</Button>
          </div>
        )}
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-red-700 flex items-center gap-2"><Warning size={20} weight="fill" /> Confirm Data Reset</DialogTitle>
            <DialogDescription className="text-zinc-600">This will permanently erase all business records on this environment. Enter your admin password to confirm.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs text-zinc-500 mb-1.5 block">Admin Password</Label>
            <Input data-testid="factory-reset-password" type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className="rounded-sm h-10" placeholder="••••••••" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button data-testid="factory-reset-confirm" onClick={doReset} disabled={resetting} className="rounded-sm bg-red-600 hover:bg-red-700 text-white">{resetting ? "Resetting…" : "Yes, delete all data"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
    <span className="text-sm text-zinc-500">{label}</span><span className="text-sm text-zinc-900 font-medium">{value || "—"}</span>
  </div>
);
